import { type JSX, useState } from 'react';

import { LauncherModal } from './LauncherModal.js';

export interface CalculatorModalProps {
  onClose: () => void;
  /** When set, footer shows a "← Back" link that pops one modal off
   *  the navigation stack instead of dismissing entirely. */
  onBack?: () => void;
}

type Op = '+' | '−' | '×' | '÷';

/**
 * Four-function calculator. The state machine is the classic "TI-style":
 *   - `display`     what's currently on screen
 *   - `accumulator` the left-hand operand (null = none stored)
 *   - `pendingOp`   the operator awaiting a right-hand operand
 *   - `freshEntry`  when true, the next digit replaces the display
 *                   (after = or after pressing an op)
 *
 * Equals chains correctly (5 + 3 = 8 → = → 11 → = → 14 with the same +3).
 * Division by zero shows "Error" without crashing.
 */
export function CalculatorModal({ onClose, onBack }: CalculatorModalProps): JSX.Element {
  const [display, setDisplay] = useState('0');
  const [accumulator, setAccumulator] = useState<number | null>(null);
  const [pendingOp, setPendingOp] = useState<Op | null>(null);
  const [freshEntry, setFreshEntry] = useState(true);
  const [lastOp, setLastOp] = useState<{ op: Op; rhs: number } | null>(null);

  function inputDigit(d: string): void {
    if (display === 'Error') reset();
    if (freshEntry) {
      setDisplay(d === '.' ? '0.' : d);
      setFreshEntry(false);
      return;
    }
    if (d === '.' && display.includes('.')) return;
    setDisplay((v) => (v === '0' && d !== '.' ? d : v + d));
  }

  function applyOp(op: Op): void {
    if (display === 'Error') return;
    const value = parseFloat(display);
    if (accumulator === null || pendingOp === null) {
      setAccumulator(value);
    } else {
      const next = compute(accumulator, value, pendingOp);
      if (next === null) {
        setDisplay('Error');
        setAccumulator(null);
        setPendingOp(null);
        setFreshEntry(true);
        return;
      }
      setAccumulator(next);
      setDisplay(formatNumber(next));
    }
    setPendingOp(op);
    setFreshEntry(true);
    setLastOp(null);
  }

  function equals(): void {
    if (display === 'Error') return;
    const value = parseFloat(display);
    if (pendingOp !== null && accumulator !== null) {
      const next = compute(accumulator, value, pendingOp);
      if (next === null) {
        setDisplay('Error');
        setAccumulator(null);
        setPendingOp(null);
        setFreshEntry(true);
        return;
      }
      setDisplay(formatNumber(next));
      setAccumulator(next);
      setLastOp({ op: pendingOp, rhs: value });
      setPendingOp(null);
      setFreshEntry(true);
    } else if (lastOp && accumulator !== null) {
      // Repeated `=` chains the last op against itself.
      const next = compute(accumulator, lastOp.rhs, lastOp.op);
      if (next === null) {
        setDisplay('Error');
        return;
      }
      setDisplay(formatNumber(next));
      setAccumulator(next);
    }
  }

  function reset(): void {
    setDisplay('0');
    setAccumulator(null);
    setPendingOp(null);
    setFreshEntry(true);
    setLastOp(null);
  }

  function negate(): void {
    if (display === 'Error' || display === '0') return;
    setDisplay((v) => (v.startsWith('-') ? v.slice(1) : `-${v}`));
  }

  return (
    <LauncherModal
      title="Calculator"
      onClose={onClose}
      testId="calculator-modal"
      {...(onBack && { onBack })}
    >
      <div className="calc">
        <div className="calc__display" data-testid="calc-display">
          {display}
        </div>
        <div className="calc__keys">
          <Key tone="util" onPress={reset}>AC</Key>
          <Key tone="util" onPress={negate}>±</Key>
          <Key tone="util" onPress={() => applyOp('÷')}>÷</Key>
          <Key tone="op"   onPress={() => applyOp('×')}>×</Key>

          <Key onPress={() => inputDigit('7')}>7</Key>
          <Key onPress={() => inputDigit('8')}>8</Key>
          <Key onPress={() => inputDigit('9')}>9</Key>
          <Key tone="op"   onPress={() => applyOp('−')}>−</Key>

          <Key onPress={() => inputDigit('4')}>4</Key>
          <Key onPress={() => inputDigit('5')}>5</Key>
          <Key onPress={() => inputDigit('6')}>6</Key>
          <Key tone="op"   onPress={() => applyOp('+')}>+</Key>

          <Key onPress={() => inputDigit('1')}>1</Key>
          <Key onPress={() => inputDigit('2')}>2</Key>
          <Key onPress={() => inputDigit('3')}>3</Key>
          <Key tone="primary" onPress={equals} double="row">=</Key>

          <Key onPress={() => inputDigit('0')} double="col">0</Key>
          <Key onPress={() => inputDigit('.')}>.</Key>
        </div>
      </div>
    </LauncherModal>
  );
}

function Key({
  children,
  onPress,
  tone,
  double,
}: {
  children: string;
  onPress: () => void;
  tone?: 'util' | 'op' | 'primary';
  double?: 'row' | 'col';
}): JSX.Element {
  const cls = [
    'calc__key',
    tone ? `calc__key--${tone}` : '',
    double === 'col' ? 'calc__key--wide' : '',
    double === 'row' ? 'calc__key--tall' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} onClick={onPress}>
      {children}
    </button>
  );
}

function compute(a: number, b: number, op: Op): number | null {
  switch (op) {
    case '+': return a + b;
    case '−': return a - b;
    case '×': return a * b;
    case '÷': return b === 0 ? null : a / b;
  }
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return 'Error';
  // Trim trailing zeros after a decimal; cap at 12 significant digits.
  const s = Number.parseFloat(n.toPrecision(12)).toString();
  return s;
}
